import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { ExcelSheetIcon } from '@assets';
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { getRowGroupStyle } from '@components/Grid/utils';
import { useNavigate } from '@solidjs/router';
import {
  exportCallingQueueApi,
  fetchApCallingQueueApi,
} from '@store/apCallingQueue';
import { dateComparator, dateFormatter } from '@store/loadboard/utils';
import { ApCallingQueueType } from '@typeDefinitions/apCallingQueueTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { handleToast } from '@utils/utils';
import { downloadCsv } from '@views/efsHistory/constants';
import { atgLogo } from '@views/ltl';
import { createSignal } from 'solid-js';

import {
  shortMonthDateFormatterCallingQueue,
  transformFilters,
} from './constants';

const columns: ColDef[] = [
  {
    headerName: 'Load',
    field: 'loadNumber',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Status',
    field: 'status',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Drop Date',
    field: 'dropDate',
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: dateComparator,
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    valueFormatter: dateFormatter,
    floatingFilter: true,
  },
  {
    headerName: 'Carrier',
    field: 'carrier',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Agent',
    field: 'setBy',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Type',
    field: 'type',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Mode',
    field: 'mode',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Attempts',
    field: 'attempts',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  },
  {
    headerName: 'Last Attempt',
    field: 'lastAttempt',
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: dateComparator,
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    valueFormatter: shortMonthDateFormatterCallingQueue,
    floatingFilter: true,
  },
];

export const CallingQueue = () => {
  const navigate = useNavigate();
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [exportLoading, setExportLoading] = createSignal<boolean>(false);
  const [gridPayload, setGridPayload] = createSignal<Record<string, unknown>>(
    {},
  );

  const onGridReady = (params: GridReadyEvent) => {
    setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: ApCallingQueueType[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          filters: [...transformFilters(params.request.filterModel)],
          sort: {
            field: orderBy || 'loadNumber',
            direction: sortBy || 'desc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 500,
        };
        setGridPayload(filteredObject);
        try {
          gridRef()?.api.showLoadingOverlay();
          const response: unknown =
            await fetchApCallingQueueApi(filteredObject);
          params.success({
            rowData: (response as { data: ApCallingQueueType[] }).data,
            rowCount: (response as { totalCount: number }).totalCount,
          });
        } catch (error) {
          params.fail();
        } finally {
          gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  const handleExport = async () => {
    const filters = { ...gridPayload() };
    delete filters.pageNumber;
    delete filters.pageSize;
    setExportLoading(true);
    const csvData = await exportCallingQueueApi(filters);
    setExportLoading(false);
    if (typeof csvData === 'string') {
      downloadCsv(csvData, 'ApCallingQueue.csv');
    } else {
      handleToast(ToastType.Error, 'Invalid CSV data received');
    }
  };

  return (
    <Stack class="h-full">
      <Box class="w-full p-2 border-gray-400 rounded-xs border-b-0 border bg-[#F8F8F8]">
        <Button
          label={
            <div class="flex items-center gap-1">
              <img src={ExcelSheetIcon} alt="excelsheet" />
              Export to Excel
            </div>
          }
          variant="contained"
          size="sm"
          onClick={handleExport}
          disabled={exportLoading()}
          isLoading={exportLoading()}
          dataTestId={formatDataTestId('Export to Excel', 'button')}
        />
      </Box>
      <Box class="ag-theme-alpine flex flex-col flex-1! min-h-[400px]">
        <AgGridSolid
          ref={gridRef}
          overlayNoRowsTemplate="No data available"
          overlayLoadingTemplate={atgLogo}
          columnDefs={columns}
          getRowStyle={getRowGroupStyle}
          rowClass={'cursor-pointer'}
          rowHeight={40}
          gridOptions={{
            defaultColDef: {
              flex: 1,
              filter: true,
              wrapText: true,
              autoHeight: true,
              cellStyle: {
                'line-height': '20px',
              },
            },
            pagination: true,
            suppressPaginationPanel: false,
            maintainColumnOrder: true,
            paginationPageSizeSelector: false,
            sideBar: {
              toolPanels: [columnsToolPanel, filtersToolPanel],
            },
            rowModelType: 'serverSide',
            paginationPageSize: 500,
            cacheBlockSize: 500,
            onGridReady: onGridReady,
          }}
          onRowClicked={(e) => {
            const orderId = (e.data as { orderId: number }).orderId;
            if (routeOrderDetails(orderId)) {
              navigate(`/order/details/${orderId}`);
            }
          }}
        />
      </Box>
    </Stack>
  );
};
